import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from 'lucide-react';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import 'react-day-picker/style.css';
import { fr } from 'react-day-picker/locale';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Day picker wrapper carrying the app's calendar styling.
 *
 * `fixedWeeks` is on by default so every month renders six week rows: without
 * it the grid grows and shrinks by a row between months, which resizes any
 * popover hosting the calendar and makes it flip sides mid-navigation.
 *
 * @param props - forwarded to `DayPicker`
 * @returns the styled calendar
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      className={cn('p-4', className)}
      classNames={{
        dropdown_root: 'rdp-dropdown_root cursor-pointer',
        dropdowns: 'flex gap-6',
        months:
          'flex flex-col relative sm:flex-row space-y-4 sm:space-y-0 sm:gap-4',
        month: 'space-y-4',
        month_caption: 'flex justify-center items-center h-7',
        caption_label: 'flex items-center gap-2 text-sm font-medium',
        nav: 'absolute top-4 left-0 right-0 flex items-center justify-between px-1 z-10 h-7',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday:
          'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal focus-within:relative focus-within:z-20 aria-selected:opacity-100 has-aria-[selected]:bg-accent first:has-aria-[selected]:rounded-l-md last:has-aria-[selected]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
        ),
        day_button: 'h-9 w-9 text-center text-sm p-0 relative',
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: 'bg-accent text-accent-foreground',
        outside:
          'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        disabled: 'text-muted-foreground opacity-50',
        range_start: 'bg-primary',
        range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground aria-selected:rounded-none',
        range_end: 'day-range-end',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: (props) => {
          if (props.orientation === 'left') {
            return <ChevronLeft className="h-4 w-4 cursor-pointer" />;
          } else if (props.orientation === 'right') {
            return <ChevronRight className="h-4 w-4 cursor-pointer" />;
          } else if (props.orientation === 'up') {
            return <ChevronUp className="h-4 w-4 cursor-pointer" />;
          } else {
            return <ChevronDown className="h-4 w-4 cursor-pointer" />;
          }
        },
      }}
      locale={fr}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
